import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ComposedChart, Line, Area, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer
} from "recharts";
import { Button, Popover, Select } from "antd";
import { companiesService, dashboardAdmissionsService } from "./../../../redux/services";
import { palette } from "../../../config/theme";
import "./index.css";

const C_ADMISSAO = palette.blue500;
const C_DEMISSAO = palette.pink500;
const C_SALDO = palette.green500;

const isAll = (v) => v === "" || v === "all" || v == null;

const nf = new Intl.NumberFormat("pt-BR");
const formatPct = (v) => `${v.toFixed(1).replace(".", ",")}%`;
const formatSigned = (v) => `${v > 0 ? "+" : ""}${nf.format(v)}`;

const SERIES = [
  { key: "admissoes", label: "Admissões", color: C_ADMISSAO },
  { key: "demissoes", label: "Demissões", color: C_DEMISSAO },
  { key: "saldo", label: "Saldo acumulado", color: C_SALDO },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="byp-turnover__tooltip">
      <div className="byp-turnover__tooltip-title">{label}</div>
      {SERIES.map((s) => {
        const item = payload.find((p) => p.dataKey === s.key);
        if (!item) return null;
        return (
          <div key={s.key} className="byp-turnover__tooltip-row">
            <span>
              <span className="byp-dot" style={{ background: s.color }} />
              {s.label}
            </span>
            <strong style={{ color: s.color }}>
              {s.key === "saldo" ? formatSigned(item.value) : nf.format(item.value)}
            </strong>
          </div>
        );
      })}
    </div>
  );
};

export default function LineStats({ empresa = "all", base = 0 }) {
  const [emp, setEmp] = useState(empresa);
  const [modo, setModo] = useState("absoluto"); // "absoluto" | "acumulado"

  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);
  const [rotatividade, setRotatividade] = useState([]);

  const [modalFilter, setModalFilter] = useState(false);
  const [empDraft, setEmpDraft] = useState(empresa);
  const [empresas, setEmpresas] = useState([{ label: "Todas as Empresas", value: "all" }]);

  async function carregaDados(nextEmp = emp) {
    setLoadingChart(true);
    try {
      const options = {};
      if (!isAll(nextEmp)) options.empresa = nextEmp;

      const resp = await dashboardAdmissionsService.getAll(options);

      const dados =
        resp?.data?.data?.rotatividade ??
        resp?.data?.rotatividade ??
        resp?.rotatividade ??
        [];

      const arr = Array.isArray(dados) ? dados : [];
      setRotatividade(arr.map((it) => ({
        competencia: String(it.competencia ?? ""),
        mes: String(it.mes ?? ""),
        admissoes: Number(it.deslig ?? 0),
        demissoes: Number(it.ativos ?? 0),
      })));
    } catch (e) {
      console.error("Erro ao carregar dados do gráfico", e);
      setRotatividade([]);
    } finally {
      setLoadingChart(false);
    }
  }

  async function carregaEmpresas() {
    setLoadingEmpresas(true);
    try {
      const response = await companiesService.getAll();
      const brutos = response?.data?.data ?? response?.data ?? response ?? [];
      const lista = (Array.isArray(brutos) ? brutos : []).map((d) => ({
        label: d.nome,
        value: d.uuid,
      }));
      setEmpresas((prev) => [prev[0], ...lista]);
    } catch (e) {
      console.error("Erro ao carregar empresas", e);
      setEmpresas([{ label: "Todas as empresas", value: "all" }]);
    } finally {
      setLoadingEmpresas(false);
    }
  }

  async function applyFilter() {
    const nextEmp = empDraft || "all";
    setEmp(nextEmp);
    setModalFilter(false);
    await carregaDados(nextEmp); // evita race com setState
  }

  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      await carregaEmpresas();
      await carregaDados(emp);
    })();
  }, []);

  const skipSyncRef = useRef(true);

  useEffect(() => {
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    setEmp(empresa);
    carregaDados(empresa);
  }, [empresa]);

  const chartData = useMemo(() => {
    let admAcc = 0;
    let demAcc = 0;

    return rotatividade.map((r) => {
      admAcc += r.admissoes;
      demAcc += r.demissoes;

      return {
        mes: r.mes,
        competencia: r.competencia,
        admissoes: modo === "acumulado" ? admAcc : r.admissoes,
        demissoes: modo === "acumulado" ? demAcc : r.demissoes,
        saldo: admAcc - demAcc,
      };
    });
  }, [rotatividade, modo]);

  const resumo = useMemo(() => {
    const totalAdmissoes = rotatividade.reduce((acc, r) => acc + r.admissoes, 0);
    const totalDemissoes = rotatividade.reduce((acc, r) => acc + r.demissoes, 0);
    const baseAtual = Number(base) || 0;

    const turnover = baseAtual > 0 && rotatividade.length
      ? rotatividade.reduce((acc, r) => acc + ((r.admissoes + r.demissoes) / 2) / baseAtual, 0)
        / rotatividade.length * 100
      : null;

    const meio = Math.floor(rotatividade.length / 2);
    const anterior = rotatividade.slice(0, meio).reduce((acc, r) => acc + r.demissoes, 0);
    const atual = rotatividade.slice(meio).reduce((acc, r) => acc + r.demissoes, 0);
    const variacao = anterior > 0 ? ((atual - anterior) / anterior) * 100 : null;

    return {
      totalAdmissoes,
      totalDemissoes,
      saldoLiquido: totalAdmissoes - totalDemissoes,
      baseAtual,
      turnover,
      variacao,
    };
  }, [rotatividade, base]);

  const competenciaBR = (c) => (c || "").split("-").reverse().join("/");
  const periodo = rotatividade.length
    ? `${competenciaBR(rotatividade[0].competencia)} - ${competenciaBR(rotatividade[rotatividade.length - 1].competencia)}`
    : "";

  const filtroContent = (
    <div className="byp-card__filter">
      <label>
        Empresa
        <Select
          value={empDraft}
          onChange={setEmpDraft}
          options={empresas}
          size="middle"
          style={{ width: "100%" }}
          loading={loadingEmpresas}
        />
      </label>
      <div className="byp-card__filter-actions">
        <Button size="small" onClick={() => setModalFilter(false)}>Cancelar</Button>
        <Button size="small" type="primary" onClick={applyFilter}>Filtrar</Button>
      </div>
    </div>
  );

  return (
    <section className="byp-card byp-turnover">
      <header className="byp-card__head">
        <div>
          <h3 className="byp-card__title">
            <span className="byp-card__title-icon"><i className="fad fa-exchange-alt" /></span>
            Estatísticas de Rotatividade{" "}
            {periodo && <span className="byp-card__title-note">({periodo})</span>}
          </h3>
        </div>
        <div className="byp-card__head-actions">
          <Select
            value={modo}
            onChange={setModo}
            size="small"
            bordered={false}
            className="byp-turnover__mode"
            options={[
              { label: "Modo: Absoluto", value: "absoluto" },
              { label: "Modo: Acumulado", value: "acumulado" },
            ]}
          />
          <Popover
            content={filtroContent}
            trigger="click"
            placement="bottomRight"
            visible={modalFilter}
            onVisibleChange={(v) => {
              if (v) setEmpDraft(emp || "all");
              setModalFilter(v);
            }}
            overlayClassName="byp-card__filter-overlay"
          >
            <button type="button" className="byp-card__action byp-card__action--icon" aria-label="Filtrar">
              <i className="far fa-filter" />
            </button>
          </Popover>
        </div>
      </header>

      {/* --- Métricas do período --- */}
      <div className="byp-turnover__metrics">
        <div className="byp-metric">
          <span className="byp-metric__label" style={{ color: C_ADMISSAO }}>Admissões</span>
          <strong className="byp-metric__value">{nf.format(resumo.totalAdmissoes)}</strong>
          <small className="byp-metric__hint">Total</small>
        </div>
        <div className="byp-metric">
          <span className="byp-metric__label" style={{ color: C_DEMISSAO }}>Demissões</span>
          <strong className="byp-metric__value">{nf.format(resumo.totalDemissoes)}</strong>
          <small className="byp-metric__hint">Total</small>
        </div>
        <div className="byp-metric">
          <span className="byp-metric__label" style={{ color: C_SALDO }}>Saldo líquido</span>
          <strong className="byp-metric__value" style={{ color: resumo.saldoLiquido < 0 ? palette.red600 : palette.green600 }}>
            {formatSigned(resumo.saldoLiquido)}
          </strong>
          <small className="byp-metric__hint">Colaboradores</small>
        </div>
        <div className="byp-metric">
          <span className="byp-metric__label" style={{ color: palette.amber600 }}>Turnover médio</span>
          <strong className="byp-metric__value">
            {resumo.turnover === null ? "—" : formatPct(resumo.turnover)}
          </strong>
          <small className="byp-metric__hint">Do período</small>
        </div>
        <div className="byp-metric byp-metric--wide">
          <span className="byp-metric__label">Variação vs período anterior</span>
          {resumo.variacao === null ? (
            <strong className="byp-metric__value">—</strong>
          ) : (
            <strong
              className="byp-metric__value"
              style={{ color: resumo.variacao > 0 ? palette.red600 : palette.green600 }}
            >
              <i className={`far ${resumo.variacao > 0 ? "fa-arrow-up" : "fa-arrow-down"}`} />{" "}
              {`${resumo.variacao > 0 ? "+" : ""}${formatPct(resumo.variacao)}`}
            </strong>
          )}
          <small className="byp-metric__hint">
            {resumo.variacao === null
              ? "Sem base de comparação"
              : resumo.variacao > 0 ? "Aumento de demissões" : "Redução de demissões"}
          </small>
        </div>
      </div>

      {/* --- Gráfico + painel lateral --- */}
      <div className="byp-turnover__body">
        <div className="byp-turnover__chart">
          <ul className="byp-turnover__legend">
            {SERIES.map((s) => (
              <li key={s.key}>
                <span className="byp-dot" style={{ background: s.color }} />
                {s.label}
              </li>
            ))}
          </ul>

          <div className="byp-turnover__plot">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="bypSaldoFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={C_SALDO} stopOpacity={0.22} />
                    <stop offset="100%" stopColor={C_SALDO} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="var(--byp-neutral-100)" />
                <XAxis
                  dataKey="mes"
                  tick={{ fill: palette.neutral400, fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fill: palette.neutral400, fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  width={44}
                />
                <Tooltip content={<CustomTooltip />} cursor={{ stroke: palette.neutral200, strokeWidth: 1 }} />
                <Area
                  type="monotone"
                  dataKey="saldo"
                  stroke={C_SALDO}
                  strokeWidth={2}
                  fill="url(#bypSaldoFill)"
                  dot={false}
                  activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2, fill: C_SALDO }}
                  animationDuration={500}
                />
                <Line
                  type="monotone"
                  dataKey="admissoes"
                  stroke={C_ADMISSAO}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2, fill: C_ADMISSAO }}
                  animationDuration={500}
                />
                <Line
                  type="monotone"
                  dataKey="demissoes"
                  stroke={C_DEMISSAO}
                  strokeWidth={2.5}
                  dot={false}
                  activeDot={{ r: 5, stroke: "#fff", strokeWidth: 2, fill: C_DEMISSAO }}
                  animationDuration={500}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        <aside className="byp-turnover__side">
          <div className="byp-turnover__side-block">
            <h4>Totais do período</h4>
            <ul>
              <li>
                <span className="byp-dot" style={{ background: C_ADMISSAO }} />
                Admissões
                <strong>{nf.format(resumo.totalAdmissoes)}</strong>
              </li>
              <li>
                <span className="byp-dot" style={{ background: C_DEMISSAO }} />
                Demissões
                <strong>{nf.format(resumo.totalDemissoes)}</strong>
              </li>
              <li>
                <span className="byp-dot" style={{ background: C_SALDO }} />
                Saldo líquido
                <strong>{formatSigned(resumo.saldoLiquido)}</strong>
              </li>
            </ul>
          </div>

          <div className="byp-turnover__side-block">
            <h4>Saldo acumulado final</h4>
            <p
              className="byp-turnover__side-value"
              style={{ color: resumo.saldoLiquido < 0 ? palette.red600 : palette.green600 }}
            >
              {formatSigned(resumo.saldoLiquido)} <small>colaboradores</small>
            </p>

            <h4>Base atual</h4>
            <p className="byp-turnover__side-value">
              {nf.format(resumo.baseAtual)} <small>colaboradores</small>
            </p>
          </div>
        </aside>
      </div>

      {loadingChart && <div className="byp-card__loading">Carregando…</div>}
    </section>
  );
}
